import React from 'react';
import { cn } from '../lib/utils';
import {
  EXPERIENCE_STATUS_TONE_CLASSES,
  type ExperienceStatusTone,
} from '../lib/experience-semantic-styles';

export type StatusType =
  | 'active'
  | 'inactive'
  | 'draft'
  | 'pending'
  | 'approved'
  | 'overdue'
  | 'completed'
  | 'cancelled'
  | 'scheduled'
  | 'in-progress'
  | 'review'
  | 'expired'
  | 'suspended';

export interface StatusBadgeProps {
  status: StatusType | (string & {});
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  dot?: boolean;
}

const statusTones: Record<string, ExperienceStatusTone> = {
  active: 'success',
  completed: 'success',
  approved: 'success',
  inactive: 'neutral',
  draft: 'neutral',
  cancelled: 'neutral',
  pending: 'warning',
  review: 'warning',
  scheduled: 'information',
  'in-progress': 'information',
  overdue: 'danger',
  expired: 'danger',
  suspended: 'warning',
};

const defaultTone: ExperienceStatusTone = 'neutral';

const legacyStatusColors: Record<string, { bg: string; text: string; dot: string }> = {
  active: {
    bg: 'bg-emerald-50 border-emerald-200',
    text: 'text-emerald-700',
    dot: 'bg-emerald-500',
  },
  completed: {
    bg: 'bg-emerald-50 border-emerald-200',
    text: 'text-emerald-700',
    dot: 'bg-emerald-500',
  },
  approved: {
    bg: 'bg-emerald-50 border-emerald-200',
    text: 'text-emerald-700',
    dot: 'bg-emerald-500',
  },
  inactive: { bg: 'bg-slate-100 border-slate-200', text: 'text-slate-600', dot: 'bg-slate-400' },
  draft: { bg: 'bg-slate-100 border-slate-200', text: 'text-slate-600', dot: 'bg-slate-400' },
  cancelled: { bg: 'bg-slate-100 border-slate-200', text: 'text-slate-600', dot: 'bg-slate-400' },
  pending: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500' },
  review: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500' },
  scheduled: { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', dot: 'bg-blue-500' },
  'in-progress': { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', dot: 'bg-blue-500' },
  overdue: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', dot: 'bg-red-500' },
  expired: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', dot: 'bg-red-500' },
  suspended: {
    bg: 'bg-orange-50 border-orange-200',
    text: 'text-orange-700',
    dot: 'bg-orange-500',
  },
};

const defaultLegacyColors = {
  bg: 'bg-slate-100 border-slate-200',
  text: 'text-slate-600',
  dot: 'bg-slate-400',
};

const sizeClasses = {
  sm: 'px-2 py-0.5 text-[10px]',
  md: 'px-2.5 py-1 text-xs',
  lg: 'px-3 py-1.5 text-sm',
};

const dotSizeClasses = {
  sm: 'h-1.5 w-1.5',
  md: 'h-2 w-2',
  lg: 'h-2.5 w-2.5',
};

function formatLabel(status: string): string {
  return status.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function StatusBadge({ status, size = 'md', className, dot = true }: StatusBadgeProps) {
  const tone = statusTones[status] ?? defaultTone;
  const legacyColors = legacyStatusColors[status] ?? defaultLegacyColors;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border font-semibold uppercase tracking-wider',
        legacyColors.bg,
        legacyColors.text,
        EXPERIENCE_STATUS_TONE_CLASSES[tone],
        sizeClasses[size],
        className,
      )}
      data-status-tone={tone}
      data-experience-tone={tone}
    >
      {dot && (
        <span
          className={cn(
            'experience-candidate-status-dot shrink-0 rounded-full',
            legacyColors.dot,
            dotSizeClasses[size],
          )}
          aria-hidden="true"
        />
      )}
      {formatLabel(status)}
    </span>
  );
}
